/*
  TB-071: the portal must start when the browser blocks site storage.

  With "block all site data" (strict privacy settings, some embedded views)
  merely reading window.localStorage / window.sessionStorage throws a
  SecurityError. The app reads storage in many places, several of them at
  module load (persisted Pinia stores, Firebase SDKs, layouts), so one throw
  leaves a blank page.

  installStorageFallback() runs first thing at start-up (see
  storageFallback.js, the first import of main.js). When a storage area is
  blocked it puts an in-memory Storage in its place for this tab, so legacy
  reads return null and writes last until the tab closes. storageAvailable()
  keeps answering for the REAL localStorage, so features that need it (sign
  in, Firebase Performance) can tell that it is missing.
*/

const AREAS = ['localStorage', 'sessionStorage']
const real = {} // area -> true when the browser's own storage works

export function createMemoryStorage () {
  const map = new Map()
  return {
    getItem: (k) => (map.has(String(k)) ? map.get(String(k)) : null),
    setItem: (k, v) => { map.set(String(k), String(v)) },
    removeItem: (k) => { map.delete(String(k)) },
    clear: () => { map.clear() },
    key: (i) => Array.from(map.keys())[i] ?? null,
    get length () { return map.size }
  }
}

// Can the area be read at all? (blocked storage throws on access or is null)
function readable (win, area) {
  try {
    const s = win[area]
    if (!s) return false
    s.getItem('__tb_storage_probe__')
    return true
  } catch (e) {
    return false
  }
}

// Can it also be written? (false when blocked, or full / quota 0)
function probe (win, area) {
  try {
    const s = win[area]
    const k = '__tb_storage_probe__'
    s.setItem(k, '1')
    s.removeItem(k)
    return true
  } catch (e) {
    return false
  }
}

// Probe each area once and replace the blocked ones with memory storage.
// Returns the list of areas that were replaced. Safe to call more than once.
export function installStorageFallback (win = typeof window !== 'undefined' ? window : undefined) {
  const replaced = []
  if (!win) return replaced
  for (const area of AREAS) {
    if (area in real) continue
    real[area] = probe(win, area)
    // Only replace storage that cannot be read: a full but readable store
    // keeps the session and settings it already holds.
    if (readable(win, area)) continue
    try {
      Object.defineProperty(win, area, {
        configurable: true,
        enumerable: true,
        value: createMemoryStorage()
      })
      replaced.push(area)
    } catch (e) {
      console.warn(`[TB-071] Could not replace blocked ${area}`)
    }
  }
  return replaced
}

// True when the browser's own localStorage works (not the memory fallback).
export function storageAvailable () {
  if (!('localStorage' in real)) {
    real.localStorage = typeof window !== 'undefined' && probe(window, 'localStorage')
  }
  return real.localStorage
}

// Test hook: forget the probe results.
export function _resetSafeStorage () {
  for (const area of AREAS) delete real[area]
}
